import migrations from '@cvent/carina-rich-text-migrations';
import DATA_TAG_REG_EXP from '@cvent/nucleus-datatag-regex';
import { css } from 'emotion';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

/**
 * Utility functions
 * https://raw.githubusercontent.com/jpuri/draftjs-to-html/master/js/common.js
 *
 * Copyright (c) 2016 Jyoti Puri
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * The function returns true if the string passed to it has no content.
 */
function isEmptyString(str) {
  if (str === undefined || str === null || str.length === 0 || str.trim().length === 0) {
    return true;
  }

  return false;
}

var _blockTypesMapping;
/**
 * The function that generates html markup for given draftjs editorContent.
 * https://github.com/jpuri/draftjs-to-html/blob/master/js/index.js
 *
 * Copyright (c) 2016 Jyoti Puri
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
// These mirror the user selectable BlockTypes constant in @cvent/carina-rich-text-editor.
// Other built in draftjs types: blockquote, code-block, atomic
// unordered-list-item and ordered-list-item are explicitly set through the list controls

var BlockTypes = {
  UNSTYLED: 'unstyled',
  PARAGRAPH: 'paragraph',
  HEADER_ONE: 'header-one',
  HEADER_TWO: 'header-two',
  HEADER_THREE: 'header-three',
  HEADER_FOUR: 'header-four',
  HEADER_FIVE: 'header-five',
  HEADER_SIX: 'header-six'
};
/**
 * Mapping block-type to corresponding html tag.
 */

var blockTypesMapping = (_blockTypesMapping = {}, _defineProperty(_blockTypesMapping, BlockTypes.UNSTYLED, 'p'), _defineProperty(_blockTypesMapping, BlockTypes.PARAGRAPH, 'p'), _defineProperty(_blockTypesMapping, BlockTypes.HEADER_ONE, 'h1'), _defineProperty(_blockTypesMapping, BlockTypes.HEADER_TWO, 'h2'), _defineProperty(_blockTypesMapping, BlockTypes.HEADER_THREE, 'h3'), _defineProperty(_blockTypesMapping, BlockTypes.HEADER_FOUR, 'h4'), _defineProperty(_blockTypesMapping, BlockTypes.HEADER_FIVE, 'h5'), _defineProperty(_blockTypesMapping, BlockTypes.HEADER_SIX, 'h6'), _defineProperty(_blockTypesMapping, 'unordered-list-item', 'ul'), _defineProperty(_blockTypesMapping, 'ordered-list-item', 'ol'), _blockTypesMapping);
/**
 * Types of links
 * Should be in sync with the LinkTypes in carina/packages/carina-rich-text-editor/src/controls/Link/constants.js
 */

var LinkTypes = {
  WEB_ADDRESS: 'WEB_ADDRESS',
  EMAIL_ADDRESS: 'EMAIL_ADDRESS'
};
/**
 * Function will return HTML tag for a block.
 */

function getBlockTag(type) {
  var typesMapping = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : blockTypesMapping;
  var baseBlockType = arguments.length > 2 ? arguments[2] : undefined;

  if (type === 'unstyled' && baseBlockType && typesMapping[baseBlockType]) {
    return typesMapping[baseBlockType];
  }

  return type && typesMapping[type];
}
/**
 * Function will return style string for a block.
 */

function getBlockStyle(data) {
  var styles = '';
  Object.keys(data).forEach(function (key) {
    if (data[key]) {
      styles += "".concat(key, ":").concat(data[key], ";");
    }
  });
  return styles;
}
/**
 * The function returns an array of entity-sections in blocks.
 * These will be areas in block which have same entity or no entity applicable to them.
 */

function getSections(block) {
  var sections = [];
  var lastOffset = 0;
  var sectionRanges = block.entityRanges.map(function (range) {
    var offset = range.offset,
        length = range.length,
        key = range.key;
    return {
      offset: offset,
      length: length,
      key: key,
      type: 'ENTITY'
    };
  });
  sectionRanges = sectionRanges.sort(function (s1, s2) {
    return s1.offset - s2.offset;
  });
  var sectionIndex = 0;
  sectionRanges.forEach(function (r) {
    if (r.offset > lastOffset) {
      sections.push({
        start: lastOffset,
        end: r.offset,
        index: sectionIndex
      });
    }

    sections.push({
      start: r.offset,
      end: r.offset + r.length,
      entityKey: r.key,
      type: r.type,
      index: sectionIndex
    });
    lastOffset = r.offset + r.length;
    sectionIndex = sectionIndex + 1;
  });

  if (lastOffset < block.text.length) {
    sections.push({
      start: lastOffset,
      end: block.text.length,
      index: sectionIndex
    });
  }

  return sections;
}
/**
 * Function to check if the block is an atomic entity block.
 */


function isAtomicEntityBlock(block) {
  if (block.entityRanges.length > 0 && isEmptyString(block.text) || block.type === 'atomic') {
    return true;
  }

  return false;
}
/**
 * The function will return the hex string of the color value in presetColors array, if there's a match.
 * Otherwise, it'll just return the value.
 */

function getColorValue(value, presetColors) {
  var hex = value;

  if (presetColors) {
    var colorObj = presetColors.find(function (color) {
      return color.value === value;
    });
    hex = colorObj ? colorObj.hex : hex;
  }

  return hex;
}
/**
 * The function will return array of inline styles applicable to the block.
 */


function getStyleArrayForBlock(_ref) {
  var block = _ref.block,
      presetColors = _ref.presetColors;
  var text = block.text,
      inlineStyleRanges = block.inlineStyleRanges;
  var inlineStyles = {
    BOLD: new Array(text.length),
    ITALIC: new Array(text.length),
    UNDERLINE: new Array(text.length),
    STRIKETHROUGH: new Array(text.length),
    SUPERSCRIPT: new Array(text.length),
    SUBSCRIPT: new Array(text.length),
    COLOR: new Array(text.length),
    BGCOLOR: new Array(text.length),
    length: text.length
  };

  if (inlineStyleRanges && inlineStyleRanges.length > 0) {
    inlineStyleRanges.forEach(function (range) {
      var offset = range.offset;
      var length = offset + range.length;

      for (var i = offset; i < length; i += 1) {
        if (range.style.indexOf('color-') === 0) {
          inlineStyles.COLOR[i] = getColorValue(range.style.split('-')[1], presetColors);
        } else if (range.style.indexOf('bgcolor-') === 0) {
          inlineStyles.BGCOLOR[i] = getColorValue(range.style.split('-')[1], presetColors);
        } else if (inlineStyles[range.style]) {
          inlineStyles[range.style][i] = true;
        }
      }
    });
  }

  return inlineStyles;
}
/**
 * The function will return inline style applicable at some offset within a block.
 */


function getStylesAtOffset(inlineStyles, offset) {
  var styles = {};

  if (inlineStyles.COLOR[offset]) {
    styles.COLOR = inlineStyles.COLOR[offset];
  }

  if (inlineStyles.BGCOLOR[offset]) {
    styles.BGCOLOR = inlineStyles.BGCOLOR[offset];
  }

  if (inlineStyles.UNDERLINE[offset]) {
    styles.UNDERLINE = true;
  }

  if (inlineStyles.ITALIC[offset]) {
    styles.ITALIC = true;
  }

  if (inlineStyles.BOLD[offset]) {
    styles.BOLD = true;
  }

  if (inlineStyles.STRIKETHROUGH[offset]) {
    styles.STRIKETHROUGH = true;
  }

  if (inlineStyles.SUBSCRIPT[offset]) {
    styles.SUBSCRIPT = true;
  }

  if (inlineStyles.SUPERSCRIPT[offset]) {
    styles.SUPERSCRIPT = true;
  }

  return styles;
}
/**
 * Function returns true for a set of styles if the value of these styles at an offset
 * are same as that on the previous offset.
 */

function sameStyleAsPrevious(inlineStyles, styles, index) {
  var sameStyled = true;

  if (index > 0 && index < inlineStyles.length) {
    styles.forEach(function (style) {
      sameStyled = sameStyled && inlineStyles[style][index] === inlineStyles[style][index - 1];
    });
  } else {
    sameStyled = false;
  }

  return sameStyled;
}
/**
 * Function returns html for text depending on inline style tags applicable to it.
 */

function addInlineStyleMarkup(style, content) {
  if (style === 'BOLD') {
    return "<span style=\"font-weight: bold;\">".concat(content, "</span>");
  } else if (style === 'ITALIC') {
    return "<span style=\"font-style: italic;\">".concat(content, "</span>");
  } else if (style === 'UNDERLINE') {
    return "<span style=\"text-decoration: underline;\">".concat(content, "</span>");
  } else if (style === 'STRIKETHROUGH') {
    return "<span style=\"text-decoration: line-through;\">".concat(content, "</span>");
  } else if (style === 'SUPERSCRIPT') {
    return "<sup>".concat(content, "</sup>");
  } else if (style === 'SUBSCRIPT') {
    return "<sub>".concat(content, "</sub>");
  }

  return content;
}
/**
 * Finds all of the matches in the given input string that match the provided regular expression.
 * @param {RegExp} regExp - Regular expression to find matches on.
 * @param {string} string - The input string to find matches in.
 * @returns {[object]]} - the array of objects of startIndex and endIndex of matches found in the string.
 */

function getAllRegExpMatches(regExp, string) {
  // See link below for why lastIndex needs to be reset to 0
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/exec#Finding_successive_matches
  // eslint-disable-next-line no-param-reassign
  regExp.lastIndex = 0;
  var match = null;
  var matches = [];
  match = regExp.exec(string);

  while (match !== null) {
    var startIndex = match.index;
    matches.push({
      startIndex: startIndex,
      endIndex: startIndex + match[0].length
    });
    match = regExp.exec(string);
  }

  return matches;
}
/**
 * The function returns text for given section of block after doing required character replacements,
 * and white space replacement to prevent white spaces from collapsing.
 * @param {boolean} isFirstSection - flag indicating whether the section is the first one of a block.
 */

function getSectionText(text, isFirstSection) {
  if (text && text.length > 0) {
    var chars = [];

    var _loop = function _loop(i) {
      var char = text[i];
      var newChar = void 0;

      switch (char) {
        case '\n':
          newChar = '<br>';
          break;

        case '&':
          newChar = '&amp;';
          break;

        case '<':
          newChar = '&lt;';
          break;

        case '>':
          newChar = '&gt;';
          break;

        default:
          newChar = char;
      } // If the text in the first section of a block starts with a white space, or the text has more than one white spaces consecutively,
      // and the text isn't inside a datatag,
      // replace the first white space and every other one following the first one with non-breaking spaces,
      // to prevent white spaces from collapsing.


      var matches = getAllRegExpMatches(DATA_TAG_REG_EXP, text.join(''));

      if (char === ' ' && !matches.find(function (match) {
        return i >= match.startIndex && i <= match.endIndex;
      }) && (i === 0 && isFirstSection || i > 0 && (chars[i - 1] !== '&nbsp;' && text[i + 1] === ' ' || chars[i - 1] === ' '))) {
        newChar = '&nbsp;';
      }

      chars.push(newChar);
    };

    for (var i = 0; i < text.length; i++) {
      _loop(i);
    }

    return chars.join('');
  }

  return '';
}
/**
 * Function returns html for text depending on inline style tags applicable to it.
 */


function addStylePropertyMarkup(styles, text) {
  if (styles && (styles.COLOR || styles.BGCOLOR)) {
    var styleString = 'style="';

    if (styles.COLOR) {
      styleString += "color: ".concat(styles.COLOR, ";");
    }

    if (styles.BGCOLOR) {
      styleString += "background-color: ".concat(styles.BGCOLOR, ";");
    }

    styleString += '"';
    return "<span ".concat(styleString, ">").concat(text, "</span>");
  }

  return text;
}
/**
 * Function will return markup for Entity.
 */

function getEntityMarkup(entityMap, entityKey, text, customEntityTransform) {
  var entity = entityMap[entityKey];

  if (typeof customEntityTransform === 'function') {
    var html = customEntityTransform(entity, text);

    if (html) {
      return html;
    }
  }

  if (entity.type === 'LINK') {
    var target = entity.data.target || '_self';
    var url = entity.data.url; // Add subject line to the email address

    if (entity.data.type === LinkTypes.EMAIL_ADDRESS) {
      var subjectLine = entity.data.subjectLine;

      if (subjectLine) {
        var encodedSubjectLine = encodeURIComponent(subjectLine);

        if (url.includes('?')) {
          url = "".concat(url, "&subject=").concat(encodedSubjectLine);
        } else {
          url = "".concat(url, "?subject=").concat(encodedSubjectLine);
        }
      }
    }

    return "<a href=\"".concat(url, "\" target=\"").concat(target, "\">").concat(text, "</a>");
  } // TODO: image support https://jira.cvent.com/browse/NUKE-135
  // if (entity.type === 'IMAGE') {
  //   return `<img src="${entity.data.src}" alt="${
  //     entity.data.alt
  //   }" style="float:${entity.data.alignment || 'none'};height: ${
  //     entity.data.height
  //   };width: ${entity.data.width}"/>`;
  // }


  return text;
}
/**
 * For a given section in a block the function will return a further list of sections,
 * with similar inline styles applicable to them.
 */

function getInlineStyleSections(_ref2) {
  var block = _ref2.block,
      styles = _ref2.styles,
      start = _ref2.start,
      end = _ref2.end,
      presetColors = _ref2.presetColors;
  var styleSections = [];
  var text = block.text;

  if (text.length > 0) {
    var inlineStyles = getStyleArrayForBlock({
      block: block,
      presetColors: presetColors
    });
    var section;

    for (var i = start; i < end; i += 1) {
      if (i !== start && sameStyleAsPrevious(inlineStyles, styles, i)) {
        section.text.push(text[i]);
        section.end = i + 1;
      } else {
        section = {
          styles: getStylesAtOffset(inlineStyles, i),
          text: [text[i]],
          start: i,
          end: i + 1
        };
        styleSections.push(section);
      }
    }
  }

  return styleSections;
}
/**
 * Replace leading blank spaces by &nbsp;
 */


function trimLeadingZeros(sectionText) {
  if (sectionText) {
    var replacedText = sectionText;

    for (var i = 0; i < replacedText.length; i += 1) {
      if (sectionText[i] === ' ') {
        replacedText = replacedText.replace(' ', '&nbsp;');
      } else {
        break;
      }
    }

    return replacedText;
  }

  return sectionText;
}
/**
 * Replace trailing blank spaces by &nbsp;
 */

function trimTrailingZeros(sectionText) {
  if (sectionText) {
    var replacedText = sectionText;

    for (var i = replacedText.length - 1; i >= 0; i -= 1) {
      if (replacedText[i] === ' ') {
        replacedText = "".concat(replacedText.substring(0, i), "&nbsp;").concat(replacedText.substring(i + 1));
      } else {
        break;
      }
    }

    return replacedText;
  }

  return sectionText;
}
/**
 * The method returns markup for section to which inline styles
 * like BOLD, ITALIC, UNDERLINE, STRIKETHROUGH, SUPERSCRIPT, SUBSCRIPT are applicable.
 */

function getStyleTagSectionMarkup(styleSection, isFirstSection) {
  var styles = styleSection.styles,
      text = styleSection.text;
  var content = getSectionText(text, isFirstSection);
  var styleKeys = Object.keys(styles); // If both superscript and subscript are applied to the string, subscript is ignore,
  // to match the behavior visually in the editor.

  if (styleKeys.includes('SUPERSCRIPT')) {
    styleKeys = styleKeys.filter(function (style) {
      return style !== 'SUBSCRIPT';
    });
  }

  styleKeys.forEach(function (style) {
    content = addInlineStyleMarkup(style, content, styles[style]);
  });
  return content;
}
/**
 * The method returns markup for section to which inline styles
 * like color, background-color are applicable.
 */


function getInlineStyleSectionMarkup(_ref3) {
  var block = _ref3.block,
      styleSection = _ref3.styleSection,
      presetColors = _ref3.presetColors,
      isFirstSection = _ref3.isFirstSection;
  var styleTagSections = getInlineStyleSections({
    block: block,
    styles: ['BOLD', 'ITALIC', 'UNDERLINE', 'STRIKETHROUGH', 'SUPERSCRIPT', 'SUBSCRIPT'],
    start: styleSection.start,
    end: styleSection.end,
    presetColors: presetColors
  });
  var styleSectionText = '';
  styleTagSections.forEach(function (stylePropertySection, index) {
    styleSectionText += getStyleTagSectionMarkup(stylePropertySection, isFirstSection && index === 0);
  });
  styleSectionText = addStylePropertyMarkup(styleSection.styles, styleSectionText);
  return styleSectionText;
}
/*
 * The method returns markup for an entity section.
 * An entity section is a continuous section in a block
 * to which same entity or no entity is applicable.
 */


function getSectionMarkup(_ref4) {
  var block = _ref4.block,
      entityMap = _ref4.entityMap,
      section = _ref4.section,
      customEntityTransform = _ref4.customEntityTransform,
      presetColors = _ref4.presetColors,
      isFirstSection = _ref4.isFirstSection;
  var entityInlineMarkup = [];
  var inlineStyleSections = getInlineStyleSections({
    block: block,
    styles: ['COLOR', 'BGCOLOR'],
    start: section.start,
    end: section.end,
    presetColors: presetColors
  });
  inlineStyleSections.forEach(function (styleSection, index) {
    entityInlineMarkup.push(getInlineStyleSectionMarkup({
      block: block,
      styleSection: styleSection,
      presetColors: presetColors,
      isFirstSection: isFirstSection && index === 0
    }));
  });
  var sectionText = entityInlineMarkup.join('');

  if (section.type === 'ENTITY') {
    if (section.entityKey !== undefined && section.entityKey !== null) {
      sectionText = getEntityMarkup(entityMap, section.entityKey, sectionText, customEntityTransform);
    }
  }

  return sectionText;
}
/**
 * Function will return the markup for block preserving the inline styles and
 * special characters like newlines or blank spaces.
 */


function getBlockInnerMarkup(_ref5) {
  var block = _ref5.block,
      entityMap = _ref5.entityMap,
      customEntityTransform = _ref5.customEntityTransform,
      presetColors = _ref5.presetColors;
  var blockMarkup = [];
  var sections = getSections(block);
  sections.forEach(function (section, index) {
    var sectionText = getSectionMarkup({
      block: block,
      entityMap: entityMap,
      section: section,
      customEntityTransform: customEntityTransform,
      presetColors: presetColors,
      isFirstSection: section.index === 0 && index === 0
    });

    if (index === 0) {
      sectionText = trimLeadingZeros(sectionText);
    }

    if (index === sections.length - 1) {
      sectionText = trimTrailingZeros(sectionText);
    }

    blockMarkup.push(sectionText);
  });
  return blockMarkup.join('');
}

/**
 * The function that generates html markup for given draftjs editorContent.
 * https://github.com/jpuri/draftjs-to-html/blob/master/js/index.js
 *
 * Copyright (c) 2016 Jyoti Puri
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * Function will return html for the block.
 */

function getBlockMarkup(_ref) {
  var block = _ref.block,
      entityMap = _ref.entityMap,
      directional = _ref.directional,
      customEntityTransform = _ref.customEntityTransform,
      presetColors = _ref.presetColors,
      baseBlockType = _ref.baseBlockType;
  var blockHtml = [];

  if (isAtomicEntityBlock(block)) {
    blockHtml.push(getEntityMarkup(entityMap, block.entityRanges[0].key, undefined, customEntityTransform));
  } else {
    var blockTag = getBlockTag(block.type, undefined, baseBlockType);

    if (blockTag) {
      blockHtml.push("<".concat(blockTag));
      var blockStyle = getBlockStyle(block.data);

      if (blockStyle) {
        blockHtml.push(" style=\"".concat(blockStyle, "\""));
      }

      if (directional) {
        blockHtml.push(' dir = "auto"');
      }

      blockHtml.push(' class="carina-rte-public-DraftStyleDefault-block"');
      blockHtml.push('>');
      var blockInnerMarkup = getBlockInnerMarkup({
        block: block,
        entityMap: entityMap,
        customEntityTransform: customEntityTransform,
        presetColors: presetColors
      });
      blockHtml.push(blockInnerMarkup || '&nbsp;');
      blockHtml.push("</".concat(blockTag, ">"));
    }
  }

  return blockHtml.join('');
}

/**
 * The function that generates html markup for a list block.
 * https://github.com/jpuri/draftjs-to-html/blob/master/js/list.js
 *
 * Copyright (c) 2016 Jyoti Puri
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * Function to check if a block is of type list.
 */

function isList(blockType) {
  return blockType === 'unordered-list-item' || blockType === 'ordered-list-item';
}
/**
 * Function will return html markup for a list block.
 */

function getListMarkup(_ref) {
  var listBlocks = _ref.listBlocks,
      entityMap = _ref.entityMap,
      directional = _ref.directional,
      customEntityTransform = _ref.customEntityTransform,
      externalStyleObject = _ref.externalStyleObject,
      presetColors = _ref.presetColors;
  var listHtml = [];
  var previousBlock;
  var closingTags = [];
  listBlocks.forEach(function (block, index) {
    var nextBlock = listBlocks[index + 1];
    var itemHasNestedBlock = nextBlock && nextBlock.depth > block.depth;

    if (!previousBlock) {
      listHtml.push("<".concat(getBlockTag(block.type)));
      listHtml.push(" class=\"carina-rte-public-DraftStyleDefault-".concat(getBlockTag(block.type), "\">"));
    } else if (block.type !== previousBlock.type && block.depth === previousBlock.depth) {
      listHtml.push("</".concat(getBlockTag(previousBlock.type), ">"));
      listHtml.push("<".concat(getBlockTag(block.type)));
      listHtml.push(" class=\"carina-rte-public-DraftStyleDefault-".concat(getBlockTag(block.type), "\">"));
    } else if (block.depth > previousBlock.depth) {
      listHtml.push("<".concat(getBlockTag(block.type)));
      listHtml.push(" class=\"carina-rte-public-DraftStyleDefault-".concat(getBlockTag(block.type), "\">"));
      closingTags.push("</".concat(getBlockTag(block.type), ">"));
    } else if (block.depth < previousBlock.depth) {
      for (var i = 0; i < previousBlock.depth - block.depth; i++) {
        listHtml.push(closingTags.pop());
        listHtml.push('</li>');
      }

      if (block.type !== previousBlock.type) {
        listHtml.push("</".concat(getBlockTag(previousBlock.type), ">"));
        listHtml.push("<".concat(getBlockTag(block.type)));
        listHtml.push(" class=\"carina-rte-public-DraftStyleDefault-".concat(getBlockTag(block.type), "\">"));
      }
    }

    listHtml.push('<li');
    var blockStyle = getBlockStyle(block.data);

    if (blockStyle) {
      listHtml.push(" style=\"".concat(blockStyle, "\""));
    }

    if (block.data.hasOwnProperty('text-align') || externalStyleObject && !block.data.hasOwnProperty('text-align')) {
      if (block.data['text-align'] === 'center') {
        listHtml.push(' class="carina-rte-center-aligned-block"');
      } else if (block.data['text-align'] === 'right') {
        listHtml.push(' class="carina-rte-right-aligned-block"');
      } else if (externalStyleObject && externalStyleObject.textAlign === 'center' && !block.data['text-align']) {
        listHtml.push(' class="carina-rte-center-aligned-block"');
      } else if (externalStyleObject && externalStyleObject.textAlign === 'right' && !block.data['text-align']) {
        listHtml.push(' class="carina-rte-right-aligned-block"');
      }
    }

    if (directional) {
      listHtml.push(' dir = "auto"');
    }

    listHtml.push('>');
    listHtml.push(getBlockInnerMarkup({
      block: block,
      entityMap: entityMap,
      customEntityTransform: customEntityTransform,
      presetColors: presetColors
    }));

    if (!itemHasNestedBlock) {
      listHtml.push('</li>');
    }

    previousBlock = block;
  });

  if (closingTags.length) {
    for (var i = 0; i < closingTags.length; i++) {
      listHtml.push(closingTags.pop());
      listHtml.push('</li>');
    }
  }

  listHtml.push("</".concat(getBlockTag(previousBlock.type), ">"));
  return listHtml.join('');
}

// Using `em` instead of `rem` to ensure spacing b/w paragraphs scales with the font size.
function rendererContainer() {
  return {
    /* Styles to reduce default margins on divs and lists. */

    /* -----Start----- */
    '& .carina-rte-public-DraftStyleDefault-block': {
      padding: '0 0 0.75em 0',
      margin: 0
    },
    '& .carina-rte-public-DraftStyleDefault-block:last-child': {
      paddingBottom: 0
    },
    '& .carina-rte-public-DraftStyleDefault-ol, & .carina-rte-public-DraftStyleDefault-ul': {
      padding: '0 0 0.75em 1.5em',
      margin: 0
    },
    '& .carina-rte-public-DraftStyleDefault-ol:last-child, & .carina-rte-public-DraftStyleDefault-ul:last-child': {
      paddingBottom: 0
    },
    '& li > .carina-rte-public-DraftStyleDefault-ul, & li > .carina-rte-public-DraftStyleDefault-ol': {
      padding: '0 0 0 1.5em'
    },
    '& .carina-rte-public-DraftStyleDefault-ol > li': {
      padding: '0.25em 0'
    },
    '& .carina-rte-public-DraftStyleDefault-ul > li': {
      padding: '0.25em 0'
    },

    /* -----End----- */

    /* Styles to keep the list numbers/bullets stay with the content when it's center or right aligned. */

    /* -----Start-----*/
    '& .carina-rte-center-aligned-block': {
      listStylePosition: 'inside'
    },
    '& .carina-rte-right-aligned-block': {
      listStylePosition: 'inside'
    },

    /* -----End-----*/

    /* Styles to reset styles for headings */

    /* -----Start-----*/
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontFamily: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      color: 'inherit'
    }
    /* -----End-----*/

  };
}
var renderStylesToString = function renderStylesToString() {
  var styleObject = rendererContainer();
  return Object.keys(styleObject).reduce(function (string, selector) {
    var newString = "".concat(string, " ").concat(selector.replace(/&/gi, '.carina-rte-styles'), " {");
    var styleDeclaration = styleObject[selector];
    newString = Object.keys(styleDeclaration).reduce(function (styles, property) {
      var value = styleDeclaration[property].toString().replace(/'/gi, '');
      var propertyName = property.replace(/[A-Z]/g, function (match) {
        return "-".concat(match.toLowerCase());
      });
      return "".concat(styles, " ").concat(propertyName, ": ").concat(value, ";");
    }, newString);
    return "".concat(newString, " }");
  }, '');
};

var styleString = renderStylesToString();
/**
 * The function that generates html markup for given draftjs editorContent.
 * https://github.com/jpuri/draftjs-to-html/blob/master/js/index.js
 *
 * Copyright (c) 2016 Jyoti Puri
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * The function will generate html markup for the data generated from carina-rich-text-editor.
 */

function draftToHtml(editorData) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      directional = _ref.directional,
      customEntityTransform = _ref.customEntityTransform,
      externalStyleObject = _ref.externalStyleObject,
      presetColors = _ref.presetColors,
      baseBlockType = _ref.baseBlockType,
      _ref$withStyles = _ref.withStyles,
      withStyles = _ref$withStyles === void 0 ? true : _ref$withStyles;

  // If editorData does not exist, just return empty string.
  if (!editorData) return ''; // If a string was passed in, just return it.

  if (typeof editorData === 'string' || editorData instanceof String) {
    return editorData;
  } // Run the object through the rich text migrations to make sure we have it in the latest form.


  var migratedData = migrations(editorData); // If content does not exist, just return empty string.

  if (!migratedData.content) return '';
  var html = [];
  var _migratedData$content = migratedData.content,
      blocks = _migratedData$content.blocks,
      entityMap = _migratedData$content.entityMap; // If there's no text in the only block, just return empty string.

  if (blocks && blocks.length === 1 && !blocks[0].text) {
    return '';
  }

  if (blocks && blocks.length > 0) {
    var listBlocks = [];
    blocks.forEach(function (block) {
      if (isList(block.type)) {
        listBlocks.push(block);
      } else {
        if (listBlocks.length > 0) {
          var listHtml = getListMarkup({
            listBlocks: listBlocks,
            entityMap: entityMap,
            customEntityTransform: customEntityTransform,
            externalStyleObject: externalStyleObject,
            presetColors: presetColors
          });
          html.push(listHtml);
          listBlocks = [];
        }

        var blockHtml = getBlockMarkup({
          block: block,
          entityMap: entityMap,
          directional: directional,
          customEntityTransform: customEntityTransform,
          presetColors: presetColors,
          baseBlockType: baseBlockType
        });
        html.push(blockHtml);
      }
    });

    if (listBlocks.length > 0) {
      var listHtml = getListMarkup({
        listBlocks: listBlocks,
        entityMap: entityMap,
        directional: directional,
        customEntityTransform: customEntityTransform,
        externalStyleObject: externalStyleObject,
        presetColors: presetColors
      });
      html.push(listHtml);
      listBlocks = [];
    }
  }

  return "<div class=".concat(withStyles ? css(rendererContainer()) : 'carina-rte-styles', ">").concat(html.join(''), "</div>");
}

/**
 * This function generates plain text from the data generated from carina-rich-text-editor.
 */

function draftToPlainText(editorData) {
  // If editorData does not exist, just return empty string.
  if (!editorData) return ''; // If a string was passed in, just return it.

  if (typeof editorData === 'string' || editorData instanceof String) {
    return editorData;
  } // Run the object through the rich text migrations to make sure we have it in the latest form.


  var migratedData = migrations(editorData); // If content does not exist, just return empty string.

  if (!migratedData.content) return ''; // Extract the plain text from the content object.

  var blocks = migratedData.content.blocks;
  return blocks.map(function (block) {
    return block ? block.text : '';
  }).join('\n');
}

var _blockTypesMapping$1;
/**
 * The function that generates html markup for given draftjs editorContent.
 * https://github.com/jpuri/draftjs-to-html/blob/master/js/index.js
 *
 * Copyright (c) 2016 Jyoti Puri
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * Function will return html for the block.
 */

/**
 * Mapping block-type to corresponding html tag.
 */

var blockTypesMapping$1 = (_blockTypesMapping$1 = {}, _defineProperty(_blockTypesMapping$1, BlockTypes.UNSTYLED, 'td'), _defineProperty(_blockTypesMapping$1, BlockTypes.PARAGRAPH, 'td'), _defineProperty(_blockTypesMapping$1, BlockTypes.HEADER_ONE, 'td'), _defineProperty(_blockTypesMapping$1, BlockTypes.HEADER_TWO, 'td'), _defineProperty(_blockTypesMapping$1, BlockTypes.HEADER_THREE, 'td'), _defineProperty(_blockTypesMapping$1, BlockTypes.HEADER_FOUR, 'td'), _defineProperty(_blockTypesMapping$1, BlockTypes.HEADER_FIVE, 'td'), _defineProperty(_blockTypesMapping$1, BlockTypes.HEADER_SIX, 'td'), _defineProperty(_blockTypesMapping$1, 'unordered-list-item', 'ul'), _defineProperty(_blockTypesMapping$1, 'ordered-list-item', 'ol'), _blockTypesMapping$1);
/**
 * Function will return email-friendly html for the block.
 */

function getBlockMarkup$1(_ref) {
  var block = _ref.block,
      entityMap = _ref.entityMap,
      directional = _ref.directional,
      customEntityTransform = _ref.customEntityTransform,
      externalBlockStyles = _ref.externalBlockStyles,
      presetColors = _ref.presetColors;
  var blockHtml = [];

  if (isAtomicEntityBlock(block)) {
    blockHtml.push(getEntityMarkup(entityMap, block.entityRanges[0].key, undefined, customEntityTransform));
  } else {
    var blockTag = getBlockTag(block.type, blockTypesMapping$1);

    if (blockTag) {
      blockHtml.push("<tr><".concat(blockTag));
      var blockStyle = getBlockStyle(block.data, blockTypesMapping$1);

      if (blockStyle || externalBlockStyles) {
        blockHtml.push(" style=\"".concat(externalBlockStyles).concat(blockStyle, "\""));
      }

      if (directional) {
        blockHtml.push(' dir = "auto"');
      }

      blockHtml.push('>');
      var blockInnerMarkup = getBlockInnerMarkup({
        block: block,
        entityMap: entityMap,
        customEntityTransform: customEntityTransform,
        presetColors: presetColors
      });
      blockHtml.push(blockInnerMarkup || '&nbsp;');
      blockHtml.push("</".concat(blockTag, "></tr>"));
    }
  }

  return blockHtml.join('');
}

/**
 * The function that generates html markup for a list block.
 * https://github.com/jpuri/draftjs-to-html/blob/master/js/list.js
 *
 * Copyright (c) 2016 Jyoti Puri
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * Function to check if a block is of type list.
 */

function isList$1(blockType) {
  return blockType === 'unordered-list-item' || blockType === 'ordered-list-item';
}
/**
 * Function will return email-friendly html markup for a list block.
 */

function getListMarkup$1(_ref) {
  var listBlocks = _ref.listBlocks,
      entityMap = _ref.entityMap,
      directional = _ref.directional,
      customEntityTransform = _ref.customEntityTransform,
      presetColors = _ref.presetColors,
      externalStyleObject = _ref.externalStyleObject;
  var listHtml = [];
  var previousBlock;
  var closingTags = [];
  listBlocks.forEach(function (block, index) {
    var nextBlock = listBlocks[index + 1];
    var itemHasNestedBlock = nextBlock && nextBlock.depth > block.depth;
    var fontSize = externalStyleObject && externalStyleObject.fontSize ? parseInt(externalStyleObject.fontSize, 10) : 16;
    var listHTMLStyle = "margin-top: 0; margin-bottom: 0; padding: 0 0 0 ".concat(Math.floor(fontSize * 1.5), "px;");

    if (!previousBlock) {
      listHtml.push("<".concat(getBlockTag(block.type), " style=\"").concat(listHTMLStyle, "\">"));
    } else if (block.type !== previousBlock.type && block.depth === previousBlock.depth) {
      listHtml.push("</".concat(getBlockTag(previousBlock.type), ">")); // Add top padding to the subsequent sibling lists.

      listHtml.push("<".concat(getBlockTag(block.type), " style=\"margin-top: 0; margin-bottom: 0; padding: ").concat(Math.floor(fontSize * 0.75), "px 0 0 ").concat(Math.floor(fontSize * 1.5), "px;\">"));
    } else if (block.depth > previousBlock.depth) {
      listHtml.push("<".concat(getBlockTag(block.type), " style=\"").concat(listHTMLStyle, "\">"));
      closingTags.push("</".concat(getBlockTag(block.type), ">"));
    } else if (block.depth < previousBlock.depth) {
      for (var i = 0; i < previousBlock.depth - block.depth; i++) {
        listHtml.push(closingTags.pop());
        listHtml.push('</li>');
      }

      if (block.type !== previousBlock.type) {
        listHtml.push("</".concat(getBlockTag(previousBlock.type), ">")); // Add top padding to the subsequent sibling lists.

        listHtml.push("<".concat(getBlockTag(block.type), " style=\"margin-top: 0; margin-bottom: 0; padding: ").concat(Math.floor(fontSize * 0.75), "px 0 0 ").concat(Math.floor(fontSize * 1.5), "px;\">"));
      }
    }

    listHtml.push('<li');
    var blockStyle = "".concat(getBlockStyle(block.data), " padding: ").concat(Math.floor(fontSize * 0.25), "px 0;");

    if (externalStyleObject && !block.data.hasOwnProperty('text-align') && (externalStyleObject.textAlign === 'center' || externalStyleObject.textAlign === 'right') || block.data.hasOwnProperty('text-align') && (block.data['text-align'] === 'center' || block.data['text-align'] === 'right')) {
      blockStyle = blockStyle + 'list-style-position: inside;';
    }

    if (blockStyle) {
      listHtml.push(" style=\"".concat(blockStyle, "\""));
    }

    if (directional) {
      listHtml.push(' dir = "auto"');
    }

    listHtml.push('>');
    listHtml.push(getBlockInnerMarkup({
      block: block,
      entityMap: entityMap,
      customEntityTransform: customEntityTransform,
      presetColors: presetColors
    }));

    if (!itemHasNestedBlock) {
      listHtml.push('</li>');
    }

    previousBlock = block;
  });

  if (closingTags.length) {
    for (var i = 0; i < closingTags.length; i++) {
      listHtml.push(closingTags.pop());
      listHtml.push('</li>');
    }
  }

  listHtml.push("</".concat(getBlockTag(previousBlock.type), ">"));
  return listHtml.join('');
}

/**
 * The function that generates html markup for given draftjs editorContent.
 * https://github.com/jpuri/draftjs-to-html/blob/master/js/index.js
 *
 * Copyright (c) 2016 Jyoti Puri
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
 * WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * Convert camelCase to hyphen-case
 */

function camelCaseToDash(str) {
  return str.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase();
}
/**
 * Convert style objects to inline styles
 * { fontSize: '16px', lineHeight: '24px' } => 'font-size: 16px;line-height: 24px;'
 * Also adding "word-break: break-word;" to break long link text in email clients
 * that support the break-word value.
 */


function getInlineStyle(styleObject) {
  var style = Object.keys(styleObject).reduce(function (inlineStyle, key) {
    var value = styleObject[key];
    return "".concat(inlineStyle).concat(camelCaseToDash(key), ":").concat(value, ";");
  }, '');
  return style ? "".concat(style, " word-break: break-word;") : 'word-break: break-word;';
}
/**
 * The function will generate email-friendly html markup for the data generated from carina-rich-text-editor.
 */

function convertToEmail(editorData) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      directional = _ref.directional,
      customEntityTransform = _ref.customEntityTransform,
      externalStyleObject = _ref.externalStyleObject,
      presetColors = _ref.presetColors;

  var externalBlockStyles = getInlineStyle(externalStyleObject || {});
  var fontSize = externalStyleObject && externalStyleObject.fontSize ? parseInt(externalStyleObject.fontSize, 10) : 16; // prettier-ignore

  var verticalPadding = Math.floor(fontSize * 0.75 / 2);
  externalBlockStyles = "".concat(externalBlockStyles, " padding: ").concat(verticalPadding, "px 0;"); // If editorData does not exist, just return empty string.

  if (!editorData) return '<tr><td></td></tr>'; // If a string was passed in, just return it.

  if (typeof editorData === 'string' || editorData instanceof String) {
    return "<tr><td style=\"".concat(externalBlockStyles, "\">").concat(editorData, "</td></tr>");
  } // Run the object through the rich text migrations to make sure we have it in the latest form.


  var migratedData = migrations(editorData); // If content does not exist, just return empty string.

  if (!migratedData.content) {
    return '<tr><td></td></tr>';
  }

  var html = [];
  var _migratedData$content = migratedData.content,
      blocks = _migratedData$content.blocks,
      entityMap = _migratedData$content.entityMap; // If there's no text in the only block, just return an empty td.

  if (blocks && blocks.length === 1 && !blocks[0].text) {
    return '<tr><td></td></tr>';
  }

  if (blocks && blocks.length > 0) {
    var listBlocks = [];
    blocks.forEach(function (block) {
      if (isList$1(block.type)) {
        listBlocks.push(block);
      } else {
        if (listBlocks.length > 0) {
          var listHtml = getListMarkup$1({
            listBlocks: listBlocks,
            entityMap: entityMap,
            directional: directional,
            customEntityTransform: customEntityTransform,
            presetColors: presetColors,
            externalStyleObject: externalStyleObject
          });
          html.push("<tr><td style=\"".concat(externalBlockStyles, "\">"));
          html.push(listHtml);
          html.push('</td></tr>');
          listBlocks = [];
        }

        var blockHtml = getBlockMarkup$1({
          block: block,
          entityMap: entityMap,
          directional: directional,
          customEntityTransform: customEntityTransform,
          externalBlockStyles: externalBlockStyles,
          presetColors: presetColors
        });
        html.push(blockHtml);
      }
    });

    if (listBlocks.length > 0) {
      var listHtml = getListMarkup$1({
        listBlocks: listBlocks,
        entityMap: entityMap,
        directional: directional,
        customEntityTransform: customEntityTransform,
        presetColors: presetColors,
        externalStyleObject: externalStyleObject
      });
      html.push("<tr><td style=\"".concat(externalBlockStyles, "\">"));
      html.push(listHtml);
      html.push('</td></tr>');
      listBlocks = [];
    }
  }

  return html.join('');
}

export default draftToHtml;
export { draftToHtml as RichTextRenderer, draftToHtml as convertToHtml, draftToPlainText as convertToPlainText, convertToEmail, BlockTypes, styleString };
//# sourceMappingURL=index.js.map
